import React, { useState, useMemo } from 'react';
import { useWizard } from '../../context/WizardContext';
import { ArrowLeft, Copy, Download, CheckCircle, FileText, MessageSquare, BarChart3, Target } from 'lucide-react';

const CAT_LABELS = {
  central_tendency: 'Central Tendency (Mean, Median, Mode)',
  dispersion: 'Dispersion & Variability (SD, Variance, Range)',
  distribution: 'Distribution Analysis (Skewness, Kurtosis)',
  frequency: 'Frequency Distributions',
  percentiles: 'Percentiles & Quartiles',
  reliability: "Reliability Analysis (Cronbach's Alpha)",
  missing_data: 'Missing Data Analysis',
  outliers: 'Outlier Detection'
};

const TEST_LABELS = {
  comparison_2groups: 'Two-Group Comparisons (t-tests, Mann-Whitney U)',
  comparison_3plus: 'Multi-Group Comparisons (ANOVA, Kruskal-Wallis)',
  correlation: 'Correlation Analysis (Pearson, Spearman)',
  regression: 'Regression Analysis',
  chi_square: 'Chi-Square Tests',
  factorial: 'Factorial Designs (Two-way ANOVA)',
  repeated_measures: 'Repeated Measures Analysis',
  nonparametric: 'Non-Parametric Alternatives'
};

const B6_ReviewExport = () => {
  const { dataB, prevStep, completeWizard, switchWizard } = useWizard();
  const [copySuccess, setCopySuccess] = useState(false);

  const analysisApproach = dataB?.analysisApproach || '';
  const qualMethods = dataB?.qualitativeMethods || {};

  const quantMethodsData = useMemo(() => dataB?.quantitativeMethods || {}, [dataB?.quantitativeMethods]);
  const dataRequirementsData = useMemo(() => dataB?.dataRequirements || { qualitative: [], quantitative: [] }, [dataB?.dataRequirements]);

  const approachLabel = {
    'qualitative': 'Qualitative',
    'quantitative': 'Quantitative', 
    'mixed': 'Mixed Methods'
  }[analysisApproach] || 'Not specified';

  const selectedQualMethods = Object.entries(qualMethods)
    .filter(([k, v]) => v)
    .map(([k]) => k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim());

  const descriptiveCats = useMemo(() => quantMethodsData.descriptiveStats?.categories || [], [quantMethodsData.descriptiveStats?.categories]);
  const inferentialTests = useMemo(() => quantMethodsData.inferentialTests?.tests || [], [quantMethodsData.inferentialTests?.tests]);

  const reportHTML = useMemo(() => {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Data Analysis Plan Report</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
      line-height: 1.6; 
      color: #1e293b; 
      background: #f8fafc;
      padding: 2rem;
    }
    .report-container {
      max-width: 800px;
      margin: 0 auto;
      background: white;
      border-radius: 16px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      overflow: hidden;
    }
    .report-header {
      background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
      color: white;
      padding: 2rem;
      text-align: center;
    }
    .report-header h1 {
      font-size: 1.75rem;
      margin-bottom: 0.5rem;
    }
    .report-header .subtitle {
      opacity: 0.9;
      font-size: 0.95rem;
    }
    .report-header .date {
      margin-top: 1rem;
      font-size: 0.85rem;
      opacity: 0.8;
    }
    .paraphrase-warning {
      background: #fef3c7;
      border-left: 4px solid #f59e0b;
      padding: 1rem 1.25rem;
      margin: 1.5rem;
      border-radius: 0 8px 8px 0;
    }
    .paraphrase-warning strong {
      color: #92400e;
      display: block;
      margin-bottom: 0.25rem;
    }
    .paraphrase-warning p {
      color: #78350f;
      font-size: 0.9rem;
    }
    .section {
      padding: 1.5rem;
      border-bottom: 1px solid #e2e8f0;
    }
    .section:last-child {
      border-bottom: none;
    }
    .section-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: #0f766e;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .section-title::before {
      content: '';
      width: 4px;
      height: 1.25rem;
      background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
      border-radius: 2px;
    }
    .approach-badge {
      display: inline-block;
      background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
      color: white;
      padding: 0.5rem 1.25rem;
      border-radius: 24px;
      font-weight: 600;
      font-size: 1rem;
    }
    .approach-description {
      margin-top: 1rem;
      color: #475569;
    }
    .method-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .method-list li {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      background: #f0fdfa;
      border-radius: 8px;
      margin-bottom: 0.5rem;
      border-left: 3px solid #14b8a6;
    }
    .method-list li::before {
      content: '✓';
      color: #0f766e;
      font-weight: bold;
    }
    .quant-section {
      background: #f0f9ff;
      border-radius: 12px;
      padding: 1.25rem;
      margin-bottom: 1rem;
    }
    .quant-section h4 {
      color: #0369a1;
      margin-bottom: 0.75rem;
      font-size: 0.95rem;
    }
    .quant-section ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .quant-section li {
      padding: 0.5rem 0;
      border-bottom: 1px solid #e0f2fe;
      color: #0c4a6e;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .quant-section li:last-child {
      border-bottom: none;
    }
    .quant-section li::before {
      content: '•';
      color: #0ea5e9;
      font-weight: bold;
    }
    .requirements-grid {
      display: grid;
      gap: 1rem;
    }
    .requirement-card {
      background: #fefce8;
      border: 1px solid #fef08a;
      border-radius: 12px;
      padding: 1rem;
    }
    .requirement-card h5 {
      color: #854d0e;
      margin-bottom: 0.5rem;
      font-size: 0.95rem;
    }
    .requirement-card p {
      color: #713f12;
      font-size: 0.85rem;
      margin-bottom: 0.5rem;
    }
    .requirement-card .question-types {
      background: white;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;
      font-size: 0.8rem;
      color: #92400e;
    }
    .next-steps {
      background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
      padding: 1.5rem;
    }
    .next-steps ol {
      margin: 0;
      padding-left: 1.5rem;
    }
    .next-steps li {
      padding: 0.5rem 0;
      color: #065f46;
    }
    .report-footer {
      background: #f1f5f9;
      padding: 1.5rem;
      text-align: center;
      color: #64748b;
      font-size: 0.85rem;
    }
    .empty-state {
      color: #94a3b8;
      font-style: italic;
      padding: 1rem;
      text-align: center;
    }
    @media print {
      body { background: white; padding: 0; }
      .report-container { box-shadow: none; border-radius: 0; }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="report-header">
      <h1>Data Analysis Plan Report</h1>
      <div class="subtitle">Generated by Research Methodology Wizard</div>
      <div class="date">${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
    </div>
    
    <div class="paraphrase-warning">
      <strong>Important: Paraphrase Before Submission</strong>
      <p>This report provides a foundation for your methodology. Always paraphrase content and add discipline-specific details before including in academic submissions.</p>
    </div>

    <div class="section">
      <h3 class="section-title">Analysis Approach</h3>
      <span class="approach-badge">${approachLabel}</span>
      <p class="approach-description">
        This research will employ a ${approachLabel.toLowerCase()} approach to data analysis${
          analysisApproach === 'mixed' ? ', combining both qualitative and quantitative methods for comprehensive insights' :
          analysisApproach === 'qualitative' ? ', focusing on in-depth understanding of themes, meanings, and experiences' :
          analysisApproach === 'quantitative' ? ', emphasizing numerical data, statistical testing, and measurable outcomes' :
          ''
        }.
      </p>
    </div>

    ${selectedQualMethods.length > 0 ? `
    <div class="section">
      <h3 class="section-title">Qualitative Analysis Methods</h3>
      <ul class="method-list">
        ${selectedQualMethods.map(method => `<li>${method}</li>`).join('')}
      </ul>
      <p style="margin-top: 1rem; color: #64748b; font-size: 0.9rem;">
        These methods require open-ended questions that allow participants to provide detailed textual responses. 
        All selected methods are available for automated analysis in Wizard F.
      </p>
    </div>
    ` : ''}

    ${(quantMethodsData.descriptiveStats?.enabled || quantMethodsData.inferentialTests?.enabled) ? `
    <div class="section">
      <h3 class="section-title">Quantitative Analysis Methods</h3>
      
      ${quantMethodsData.descriptiveStats?.enabled && descriptiveCats.length > 0 ? `
      <div class="quant-section">
        <h4>Descriptive Statistics</h4>
        <ul>
          ${descriptiveCats.map(cat => `<li>${CAT_LABELS[cat] || cat}</li>`).join('')}
        </ul>
      </div>
      ` : ''}
      
      ${quantMethodsData.inferentialTests?.enabled && inferentialTests.length > 0 ? `
      <div class="quant-section" style="background: #fdf4ff;">
        <h4 style="color: #86198f;">Inferential Statistics</h4>
        <ul>
          ${inferentialTests.map(test => `<li style="color: #701a75; border-color: #f5d0fe;">${TEST_LABELS[test] || test}</li>`).join('')}
        </ul>
      </div>
      ` : ''}
    </div>
    ` : ''}

    <div class="section">
      <h3 class="section-title">Data Collection Requirements</h3>
      <p style="color: #475569; margin-bottom: 1rem; font-size: 0.9rem;">
        To support your selected analysis methods, your survey should include:
      </p>
      
      ${dataRequirementsData.qualitative.length > 0 || dataRequirementsData.quantitative.length > 0 ? `
      <div class="requirements-grid">
        ${dataRequirementsData.qualitative.map(req => `
          <div class="requirement-card">
            <h5>${req.label}</h5>
            <p>${req.description}</p>
            <div class="question-types"><strong>Question Types:</strong> ${req.wizardFType}</div>
          </div>
        `).join('')}
        ${dataRequirementsData.quantitative.map(req => `
          <div class="requirement-card" style="background: #eff6ff; border-color: #bfdbfe;">
            <h5 style="color: #1e40af;">${req.label}</h5>
            <p style="color: #1e3a8a;">${req.description}</p>
            <div class="question-types" style="color: #1d4ed8;"><strong>Question Types:</strong> ${req.wizardFType}${req.examples ? `<br><strong>Examples:</strong> ${req.examples.join(', ')}` : ''}</div>
          </div>
        `).join('')}
      </div>
      ` : '<div class="empty-state">No specific data requirements identified yet.</div>'}
    </div>

    <div class="next-steps">
      <h3 class="section-title" style="color: #065f46;">Next Steps</h3>
      <ol>
        <li>Build your survey in Wizard F using the question types identified above</li>
        <li>Distribute your survey and collect responses</li>
        <li>Use Wizard F's Analytics panel to run your planned analyses</li>
        <li>All qualitative methods are available for automated analysis in Wizard F</li>
        <li>Export your results in APA 7th edition format for your research report</li>
      </ol>
    </div>

    <div class="report-footer">
      <p>End of Data Analysis Plan Report</p>
      <p style="margin-top: 0.5rem;">Use this document as a guide when building your survey and conducting analysis.</p>
    </div>
  </div>
</body>
</html>`;
  }, [approachLabel, analysisApproach, selectedQualMethods, quantMethodsData, descriptiveCats, inferentialTests, dataRequirementsData]);

  const getPlainText = () => {
    let text = `DATA ANALYSIS PLAN REPORT
Generated by Research Methodology Wizard
Date: ${new Date().toLocaleDateString()}

ANALYSIS APPROACH
-----------------
Selected Approach: ${approachLabel}

This research will employ a ${approachLabel.toLowerCase()} approach to data analysis.
`;

    if (selectedQualMethods.length > 0) {
      text += `
QUALITATIVE ANALYSIS METHODS
----------------------------
The following qualitative analysis methods have been selected:

`;
      selectedQualMethods.forEach((method, index) => {
        text += `${index + 1}. ${method}\n`;
      });
      text += `
These methods require open-ended questions that allow participants to provide 
detailed textual responses.

`;
    }

    if (quantMethodsData.descriptiveStats?.enabled || quantMethodsData.inferentialTests?.enabled) {
      text += `
QUANTITATIVE ANALYSIS METHODS
-----------------------------
`;
      if (quantMethodsData.descriptiveStats?.enabled && descriptiveCats.length > 0) {
        text += `Descriptive Statistics:
`;
        descriptiveCats.forEach((cat, index) => {
          text += `  ${index + 1}. ${CAT_LABELS[cat] || cat}\n`;
        });
        text += '\n';
      }

      if (quantMethodsData.inferentialTests?.enabled && inferentialTests.length > 0) {
        text += `Inferential Statistics:
`;
        inferentialTests.forEach((test, index) => {
          text += `  ${index + 1}. ${TEST_LABELS[test] || test}\n`;
        });
        text += '\n';
      }
    }

    text += `
DATA COLLECTION REQUIREMENTS
----------------------------
`;
    if (dataRequirementsData.qualitative.length > 0) {
      dataRequirementsData.qualitative.forEach(req => {
        text += `- ${req.label}: ${req.description}\n  Question Types: ${req.wizardFType}\n\n`;
      });
    }
    if (dataRequirementsData.quantitative.length > 0) {
      dataRequirementsData.quantitative.forEach(req => {
        text += `- ${req.label}: ${req.description}\n  Question Types: ${req.wizardFType}\n`;
        if (req.examples) {
          text += `  Examples: ${req.examples.join(', ')}\n`;
        }
        text += '\n';
      });
    }

    text += `
NEXT STEPS
----------
1. Build your survey in Wizard F using the question types identified above
2. Distribute your survey and collect responses
3. Use Wizard F's Analytics panel to run your planned analyses
4. All qualitative methods are available for automated analysis in Wizard F
5. Export your results in APA 7th edition format for your research report
`;

    return text;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getPlainText());
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([reportHTML], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    element.download = "data_analysis_plan.html";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleComplete = () => {
    completeWizard('B');
    
    const proceedToC = window.confirm(
      'Wizard B Complete!\n\n' +
      'Your data analysis plan has been saved. Would you like to proceed to Wizard C (Framework Builder)?'
    );
    if (proceedToC) {
      switchWizard('C');
    }
  };

  return (
    <div className="wizard-step">
      <h2>Step 7: Review & Export</h2>
      <p className="step-description">
        Review your complete data analysis plan and download it for your records.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <Target size={24} color="#3b82f6" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>Approach</div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>{approachLabel}</div>
        </div>

        <div style={{
          background: '#f5f3ff',
          borderRadius: '12px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <MessageSquare size={24} color="#7c3aed" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>Qualitative Methods</div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>{selectedQualMethods.length}</div>
        </div>

        <div style={{
          background: '#ecfeff',
          borderRadius: '12px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <BarChart3 size={24} color="#0891b2" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>Quantitative Categories</div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>{descriptiveCats.length + inferentialTests.length}</div>
        </div>

        <div style={{
          background: '#ecfdf5',
          borderRadius: '12px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <FileText size={24} color="#059669" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>Data Requirements</div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>
            {dataRequirementsData.qualitative.length + dataRequirementsData.quantitative.length}
          </div>
        </div>
      </div>

      <div style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.25rem',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc'
        }}>
          <h3 style={{ margin: 0 }}>Data Analysis Plan Report</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleCopy} style={{ padding: '0.5rem 1rem' }}>
              <Copy size={16} />
              {copySuccess ? 'Copied!' : 'Copy Text'}
            </button>
            <button className="btn btn-secondary" onClick={handleDownload} style={{ padding: '0.5rem 1rem' }}>
              <Download size={16} />
              Download HTML
            </button>
          </div>
        </div>
        <div style={{
            maxHeight: '500px',
            overflow: 'auto',
            background: '#f8fafc'
          }}
        >
          <iframe
            srcDoc={reportHTML}
            style={{
              width: '100%',
              height: '500px',
              border: 'none'
            }}
            title="Data Analysis Plan Report Preview"
          />
        </div>
      </div>

      <div style={{
        background: '#ecfdf5',
        border: '1px solid #6ee7b7',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <CheckCircle size={24} color="#059669" style={{ flexShrink: 0 }} />
        <div>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#065f46' }}>Your Analysis Plan is Complete!</h4>
          <p style={{ margin: 0, color: '#047857', fontSize: '0.9rem' }}>
            Click "Complete Wizard B" to save your selections. Your analysis plan will be available 
            as a reminder in Wizard F when you build your survey, and in the combined report.
          </p>
        </div>
      </div>

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button className="btn btn-primary" onClick={handleComplete}>
          <CheckCircle size={18} />
          Complete Wizard B
        </button>
      </div>
    </div>
  );
};

export default B6_ReviewExport;
